import fahrzeugtyp from '@ahd/shared/maps/fahrzeugtyp';
import type { Car } from '@ahd/shared/types/ahd-cars/car/car';
import Badge from '@mui/material/Badge';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { getActivePortals, getFirma4Id, getIdFirma, getMwSt, getStandorteForSelect, isDatenexport } from '../_tools/Session';
import { formatText, getElementFromArray, getFieldFromArray } from '../_tools/Texte';
import { checkUndefinedNullOrEmpty, checkUndefinedOrNull, getCarThumbnail } from '../_tools/Tools';
import { type Vertrage, getVertrag } from '../_tools/Vertrag';
import { ColumnWidths, type DataGridEnrichedColDef, type DataGridProps } from '../_tools/datagrid/types';
import InputImage from '../_tools/inputfields/Image';
import { Effizienz, type EffizienzClass, effizienzSteps } from '../components/effizienz';
import Iconify from '../components/iconify/component';
import { type IconifyProps } from '../components/iconify/types';
import { type CustomTheme } from '../provider/ThemeProvider';
import sExtLackart from './s_ext_lackart';
import sFdKsb from './s_fdKsb';
import sIntAIPLevel from './s_int_AIPLevel';
import sIntAktivStatus from './s_int_aktivStatus';
import sIntAufbau from './s_int_aufbau';
import sIntBattery from './s_int_battery';
import sIntColors from './s_int_colors';
import sIntExportStatus from './s_int_exportStatus';
import sIntGetriebe from './s_int_getriebe';
import sIntHereinnahme from './s_int_hereinnahme';
import sIntKraftstoff from './s_int_kraftstoff';
import sIntMotor from './s_int_motor';
import sIntPlakette from './s_int_plakette';
import sIntPlugTypes from './s_int_plugTypes';
import sIntPolster from './s_int_polster';
import sIntProblemlevel from './s_int_problemlevel';
import sIntReserviertStatus from './s_int_reserviertStatus';
import sIntSchadstoff from './s_int_schadstoff';
import sIntSiegel from './s_int_siegel';
import sIntSteuer from './s_int_steuer';
import sPortalId from './s_portalID';
import sStatusExport from './s_statusExport';
import sVerwendung from './s_verwendung';

function sSpaltenFahrzeugliste(
    theme: CustomTheme,
    vertrage: Vertrage,
    additional?: {
        changeExportStatusPortal?: (id: Car['id'], idPortal: number, aktStatus: boolean) => void;
        ec?: number;
        openCarProblem?: (car: Car) => void;
        setCar?: (car: Car) => void;
        setShowBilderCar?: (car: Car) => void;
        setShowPreisschild?: (state: boolean) => void;
        setVisibleBilder?: (state: boolean) => void;
    }
): DataGridProps['columns'] {
    return [
        {
            headerName: 'Bild',
            group: 'Bilder',
            field: 'urThumb',
            type: 'image',
            hideOnDensityCompact: true,
            width: ColumnWidths.s,
            noCSV: true,
            renderCell(params) {
                const carImg = getCarThumbnail(
                    {
                        deleted: params?.row?.deleted,
                        dv: params?.row?.dv,
                        imgs: params?.row?.imgs,
                        se: params?.row?.se,
                    },
                    location,
                    additional?.ec
                );
                const carImgUrl = carImg && (carImg.uThumb || carImg.url);
                const hasImgs = [];
                const ignore = [3, 7, 30]; // 3: Füllbilder, 7: PLaceholder, 30: Schadensbilder
                if (params?.row?.imgs) {
                    for (let i = 0; i < params?.row?.imgs.length; i++) {
                        const bild = params?.row?.imgs[i];
                        if (ignore.includes(bild.typ) || (bild.typ === 0 && bild.nr === 0)) continue; // 0: EyeCatcher beim ersten Bild
                        hasImgs.push(bild);
                    }
                }
                return (
                    <InputImage
                        overlay={
                            !checkUndefinedNullOrEmpty(hasImgs)
                                ? [
                                      {
                                          title: 'Alle Bilder anzeigen',
                                          icon: 'mdi:fullscreen',
                                          func: () => {
                                              additional?.setShowBilderCar({ ...params?.row, imgs: hasImgs });
                                              additional?.setVisibleBilder(true);
                                          },
                                      },
                                  ]
                                : undefined
                        }
                        v={carImgUrl}
                    />
                );
            },
        },
        {
            headerName: 'Bilderanzahl',
            group: 'Bilder',
            field: 'bildCount',
            width: ColumnWidths.xs,
            renderCell(params) {
                return (
                    <Typography component="span" variant="body1" sx={{ color: params?.row?.bildCount === 0 ? 'error.main' : undefined, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {params?.row?.bildCount || '-'}
                    </Typography>
                );
            },
        },
        { headerName: 'Auftragsnummer', group: 'Fahrzeugdaten', width: ColumnWidths.s, field: 'nr' },
        { headerName: 'VIN', group: 'Fahrzeugdaten', field: 'ch', width: ColumnWidths.m },
        { headerName: 'Fahrzeugstatus', group: 'Fahrzeugdaten', field: 'st', width: ColumnWidths.s },
        { headerName: 'Fahrzeugtyp', group: 'Fahrzeugdaten', field: 'tp', width: ColumnWidths.l, type: 'singleselect', data: fahrzeugtyp },
        { headerName: 'Ankaufart', group: 'Fahrzeugdaten', field: 'herk', width: ColumnWidths.s, type: 'singleselect', data: sIntHereinnahme },
        { headerName: 'Hersteller', group: 'Fahrzeugdaten', field: 'hs', width: ColumnWidths.s },
        { headerName: 'Modell', group: 'Fahrzeugdaten', field: 'md', width: ColumnWidths.m },
        { headerName: 'Ausführung', group: 'Fahrzeugdaten', field: 'dsgn', width: ColumnWidths.l },
        { headerName: 'Trimline', group: 'Fahrzeugdaten', field: 'trimline', width: ColumnWidths.l },
        { headerName: 'Modelrange', group: 'Fahrzeugdaten', field: 'modelrange', width: ColumnWidths.l },
        {
            headerName: 'Ausstattungstexte',
            group: 'Fahrzeugdaten',
            field: 'desc',
            width: ColumnWidths.l,
            renderCell: (params) => {
                if (checkUndefinedNullOrEmpty(params?.row?.desc)) {
                    return '';
                } else {
                    return [
                        { i: 'highlights', t: 'Highlights' },
                        { i: 'assistenz', t: 'Assistenzsysteme' },
                        { i: 'multimedia', t: 'Multimedia' },
                        { i: 'aussen', t: 'Außenausstattung' },
                        { i: 'scheinwerfer', t: 'Scheinwerfer & Leuchten' },
                        { i: 'raeder', t: 'Räder & Reifen' },
                        { i: 'innen', t: 'Innenausstattung' },
                        { i: 'sitze', t: 'Sitze & Polster' },
                        { i: 'technik', t: 'Technik & Sicherheit' },
                        { i: 'pakete', t: 'Pakete' },
                        { i: 'sonstiges', t: 'Sonstiges' },
                        { i: 'fussnoten', t: 'Fußnoten' },
                    ]
                        .filter((key) => !checkUndefinedNullOrEmpty(params?.row?.desc?.[key?.i]))
                        .map((key) => {
                            return `${key?.t}: ${params?.row?.desc?.[key?.i]}`;
                        })
                        ?.join(' // ');
                }
            },
            valueFormatter(params) {
                if (checkUndefinedNullOrEmpty(params?.value)) {
                    return '';
                } else {
                    return [
                        { i: 'highlights', t: 'Highlights' },
                        { i: 'assistenz', t: 'Assistenzsysteme' },
                        { i: 'multimedia', t: 'Multimedia' },
                        { i: 'aussen', t: 'Außenausstattung' },
                        { i: 'scheinwerfer', t: 'Scheinwerfer & Leuchten' },
                        { i: 'raeder', t: 'Räder & Reifen' },
                        { i: 'innen', t: 'Innenausstattung' },
                        { i: 'sitze', t: 'Sitze & Polster' },
                        { i: 'technik', t: 'Technik & Sicherheit' },
                        { i: 'pakete', t: 'Pakete' },
                        { i: 'sonstiges', t: 'Sonstiges' },
                        { i: 'fussnoten', t: 'Fußnoten' },
                    ]
                        .filter((key) => !checkUndefinedNullOrEmpty(params?.value?.[key?.i]))
                        .map((key) => {
                            return `${key?.t}: ${params?.value?.[key?.i]}`;
                        })
                        ?.join(' // ');
                }
            },
        },
        {
            headerName: 'Firma',
            group: 'Fahrzeugdaten',
            field: 'se.i',
            width: ColumnWidths.l,
            renderCell: (params) =>
                !checkUndefinedOrNull(getFirma4Id(params?.row?.se?.i)) ? (getFirma4Id(params?.row?.se?.i).al ? getFirma4Id(params?.row?.se?.i).al : getFirma4Id(params?.row?.se?.i).n) : '',
            valueFormatter: (params) => (!checkUndefinedOrNull(getFirma4Id(params?.value)) ? (getFirma4Id(params?.value).al ? getFirma4Id(params?.value).al : getFirma4Id(params?.value).n) : ''),
        },
        { headerName: 'Standort', group: 'Fahrzeugdaten', field: 'so', width: ColumnWidths.l, type: 'singleselect', data: getStandorteForSelect() },
        { headerName: 'Standortbemerkung', group: 'Fahrzeugdaten', field: 'locTxt', width: ColumnWidths.m },
        { headerName: 'Standortwechsel', group: 'Fahrzeugdaten', field: 'stat.sw', width: ColumnWidths.s, type: 'date' },
        { headerName: 'Parkplatz', group: 'Fahrzeugdaten', field: 'snr', width: ColumnWidths.s },
        { headerName: 'Aufbau', group: 'Fahrzeugdaten', field: 'iab', width: ColumnWidths.m, type: 'singleselect', data: sIntAufbau },
        { headerName: 'Motorart', group: 'Fahrzeugdaten', field: 'imtr', width: ColumnWidths.m, type: 'singleselect', data: sIntMotor },
        { headerName: 'Kraftstoff', group: 'Fahrzeugdaten', field: 'vb.iks', width: ColumnWidths.m, type: 'singleselect', data: sIntKraftstoff },
        { headerName: 'Getriebe', group: 'Fahrzeugdaten', field: 'itr', width: ColumnWidths.m, type: 'singleselect', data: sIntGetriebe },
        { headerName: 'Hubraum', group: 'Fahrzeugdaten', field: 'hb', width: ColumnWidths.m, type: 'decimal', einheit: 'cm³' },
        {
            headerName: 'Leistung',
            group: 'Fahrzeugdaten',
            field: 'pw',
            width: ColumnWidths.m,
            renderCell: (params) => {
                const leistungKW = params?.row?.pw ? Math.round(params?.row?.pw) : 0;
                const leistungPS = params?.row?.pw ? Math.round(params?.row?.pw * 1.36) : '-';
                return leistungKW + ' kW / ' + leistungPS + ' PS';
            },
            valueFormatter: (params) => {
                const leistungKW = params?.value ? Math.round(params?.value) : 0;
                const leistungPS = params?.value ? Math.round(params?.value * 1.36) : '-';
                return leistungKW + ' kW / ' + leistungPS + ' PS';
            },
        },
        { headerName: 'Erstzulassung', group: 'Fahrzeugdaten', field: 'ez', width: ColumnWidths.m, type: 'date' },
        { headerName: 'Baujahr', group: 'Fahrzeugdaten', field: 'bj', width: ColumnWidths.s },
        { headerName: 'Modelljahr', group: 'Fahrzeugdaten', field: 'mj', width: ColumnWidths.s },
        { headerName: 'Laufleistung', group: 'Fahrzeugdaten', field: 'km', width: ColumnWidths.s, type: 'decimal', einheit: 'km' },
        { headerName: 'Kennzeichen', group: 'Fahrzeugdaten', field: 'knz', width: ColumnWidths.s },
        { headerName: 'Grundfarbe', group: 'Fahrzeugdaten', field: 'far.b', width: ColumnWidths.m },
        { headerName: 'Außenfarbe', group: 'Fahrzeugdaten', field: 'far.f', width: ColumnWidths.m },
        { headerName: 'Lackart', group: 'Fahrzeugdaten', field: 'far.il', width: ColumnWidths.m, type: 'singleselect', data: sExtLackart },
        { headerName: 'Polster', group: 'Fahrzeugdaten', field: 'ipo', width: ColumnWidths.m, type: 'singleselect', data: sIntPolster },
        { headerName: 'Polsterfarbe', group: 'Fahrzeugdaten', field: 'ifar.if', width: ColumnWidths.m, type: 'singleselect', data: sIntColors },
        { headerName: 'Vorbesitzer', group: 'Fahrzeugdaten', field: 'vo', width: ColumnWidths.s },
        { headerName: 'Bemerkungen', group: 'Fahrzeugdaten', field: 'hi', width: ColumnWidths.m },
        {
            headerName: 'Schäden',
            group: 'Fahrzeugdaten',
            field: 'dmsg',
            width: ColumnWidths.xs,
            renderCell(params) {
                return (
                    <Typography component="span" variant="body1" sx={{ color: params?.row?.dmsg === 0 ? 'success.main' : 'error.main', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {params?.row?.dmsg === 0 ? 'unfallfrei' : params?.row?.dmsg}
                    </Typography>
                );
            },
            valueFormatter(params) {
                return params?.value === 0 ? 'unfallfrei' : params?.value;
            },
        },
        {
            headerName: 'Standtage I',
            group: 'Statistik',
            field: 'stat.st1',
            width: ColumnWidths.s,
            renderCell(params) {
                return params?.row?.stat?.st1 ? (
                    <Typography
                        component="span"
                        variant="body1"
                        sx={{
                            color:
                                getIdFirma() === 11700
                                    ? params?.row?.stat?.st1 >= 15
                                        ? 'error.main'
                                        : params?.row?.stat?.st1 >= 11
                                          ? 'warning.main'
                                          : 'success.main'
                                    : params?.row?.stat?.st1 >= 90
                                      ? 'error.main'
                                      : params?.row?.stat?.st1 >= 60
                                        ? 'warning.main'
                                        : params?.row?.stat?.st1 >= 30
                                          ? 'warning.lightest'
                                          : undefined,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {`${params?.row?.stat?.st1} Tag${params?.row?.stat?.st1 === 1 ? ' ' : 'e'}`}
                    </Typography>
                ) : (
                    '-'
                );
            },
            valueFormatter(params) {
                return params?.value ? `${params?.value} Tag${params?.value === 1 ? ' ' : 'e'}` : '-';
            },
        },
        {
            headerName: 'Standtage',
            group: 'Statistik',
            field: 'stat.st2',
            width: ColumnWidths.s,
            renderCell(params) {
                return params?.row?.stat?.st2 ? (
                    <Typography
                        component="span"
                        variant="body1"
                        sx={{
                            color: params?.row?.stat?.st2 >= 90 ? 'error.main' : params?.row?.stat?.st2 >= 60 ? 'warning.main' : params?.row?.stat?.st2 >= 30 ? 'warning.lightest' : undefined,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {`${params?.row?.stat?.st2} Tag${params?.row?.stat?.st2 === 1 ? ' ' : 'e'}`}
                    </Typography>
                ) : (
                    '-'
                );
            },
            valueFormatter(params) {
                return params?.value ? `${params?.value} Tag${params?.value === 1 ? ' ' : 'e'}` : '-';
            },
        },
        {
            headerName: 'Preistage',
            group: 'Statistik',
            field: 'stat.pt',
            width: ColumnWidths.s,
            renderCell(params) {
                return params?.row?.stat?.pt ? (
                    <Typography
                        component="span"
                        variant="body1"
                        sx={{
                            color: params?.row?.stat?.pt >= 90 ? 'error.main' : params?.row?.stat?.pt >= 60 ? 'warning.main' : params?.row?.stat?.pt >= 30 ? 'warning.lightest' : undefined,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {`${params?.row?.stat?.pt} Tag${params?.row?.stat?.pt === 1 ? ' ' : 'e'}`}
                    </Typography>
                ) : (
                    '-'
                );
            },
            valueFormatter(params) {
                return params?.value ? `${params?.value} Tag${params?.value === 1 ? ' ' : 'e'}` : '-';
            },
        },
        { headerName: 'Inventur-Erfassungsdatum', group: 'Statistik', field: 'stat.dinv', width: ColumnWidths.s, type: 'date' },
        { headerName: 'Inventur-Erfasser', group: 'Statistik', field: 'stat.uinv', width: ColumnWidths.s, type: 'date' },
        { headerName: 'Verfügbar ab', group: 'Statistik', field: 'stat.verfugbar', width: ColumnWidths.s, type: 'date' },
        { headerName: 'Zugang geplant', group: 'Statistik', field: 'stat.zg', width: ColumnWidths.s, type: 'date' },
        { headerName: 'Siegel', group: 'Fahrzeugdaten', field: 'sg.i', width: ColumnWidths.s, type: 'singleselect', data: sIntSiegel },
        { headerName: 'Verwendung', group: 'Fahrzeugdaten', field: 'v', width: ColumnWidths.s, type: 'singleselect', data: sVerwendung(getIdFirma() === 53880) },
        {
            headerName: 'Warnungen',
            group: 'Fahrzeugdaten',
            field: 'warnings',
            disableCellClick: true,
            width: ColumnWidths.xs,
            type: 'singleselect',
            data: sIntProblemlevel,
            renderCell(params) {
                const warningElement = getElementFromArray(sIntProblemlevel, params?.row?.warnings, 'i', undefined);
                return (
                    <Tooltip title={warningElement?.t} placement="bottom">
                        <span>
                            <IconButton
                                onClick={() => {
                                    additional?.openCarProblem(params?.row);
                                }}
                            >
                                <Iconify color={warningElement?.iconColor} icon={warningElement?.icon} />
                            </IconButton>
                        </span>
                    </Tooltip>
                );
            },
        },
        {
            headerName: 'e-Preisschild',
            group: 'Fahrzeugdaten',
            field: 'aip',
            disableCellClick: true,
            width: ColumnWidths.xs,
            type: 'singleselect',
            data: sIntAIPLevel,
            renderCell(params) {
                const preisschildElement = getElementFromArray(sIntAIPLevel, params?.row?.aip, 'i', undefined);
                return (
                    <Tooltip title={preisschildElement?.t}>
                        <span>
                            <IconButton
                                onClick={() => {
                                    additional?.setCar(params?.row);
                                    additional?.setShowPreisschild(true);
                                }}
                            >
                                <Iconify color={preisschildElement?.iconColor} icon={preisschildElement?.icon} />
                            </IconButton>
                        </span>
                    </Tooltip>
                );
            },
        },
        { headerName: 'Garantie', group: 'Fahrzeugdaten', field: 'gar.g', width: ColumnWidths.s },
        {
            headerName: 'Export',
            group: 'Export',
            field: 'export.exp',
            disableCellClick: true,
            width: ColumnWidths.xs,
            type: 'singleselect',
            data: sStatusExport,
            renderCell(params) {
                const exportStatus = params?.row?.export?.exp;
                const exportError = params?.row?.export?.err;
                const exportElement = getElementFromArray(sStatusExport, exportStatus, 'i', undefined);

                return (
                    <Tooltip title={formatText(exportElement?.t, !checkUndefinedNullOrEmpty(exportError) ? '\nFehler: ' + exportError : '')} PopperProps={{ sx: { whiteSpace: 'pre' } }}>
                        <span>
                            <IconButton
                                disabled={!isDatenexport()}
                                onClick={() => {
                                    additional?.changeExportStatusPortal(params?.row?.id, 0, exportStatus !== 2);
                                }}
                            >
                                <Badge
                                    overlap="circular"
                                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                    badgeContent={
                                        !checkUndefinedNullOrEmpty(exportError) ? (
                                            <Iconify color="error.main" icon="mdi:alert-circle" sx={{ backgroundColor: '#fff', borderRadius: 10 }} width={12.5} height={12.5} />
                                        ) : undefined
                                    }
                                >
                                    <Iconify color={exportElement?.iconColor} icon={exportElement?.icon} />
                                </Badge>
                            </IconButton>
                        </span>
                    </Tooltip>
                );
            },
        },
        ...getActivePortals()
            .map((portal) => {
                const name = getFieldFromArray(sPortalId, 't', portal, 'i', undefined) as string;
                const icon = getFieldFromArray(sPortalId, 'icon', portal, 'i', undefined) as IconifyProps;
                const zugang = (getFieldFromArray(sPortalId, 'et', portal, 'i', undefined) as string) || '';
                const group = (getFieldFromArray(sPortalId, 'group', portal, 'i', undefined) as string) || '';
                const groupSort = group.toLowerCase().includes('mobile.de')
                    ? 'aaaaaa'
                    : group.toLowerCase().includes('autoscout') || group.toLowerCase().includes('as24')
                      ? 'aaaaab'
                      : group.toLowerCase().includes('heycar')
                        ? 'aaaaac'
                        : group.toLowerCase().includes('gewato')
                          ? 'aaaaad'
                          : 'zzzzzzzzzz';
                return {
                    iconHeaderName: (
                        <Stack>
                            {!checkUndefinedNullOrEmpty(icon) ? <Iconify icon={icon} /> : <Typography variant="body2">{name}</Typography>}
                            <Typography variant="caption">{zugang}</Typography>
                        </Stack>
                    ),
                    headerName: [name, zugang.replace('Zugang', 'Zg.')].join(' '),
                    group: 'Export',
                    groupSort: groupSort,
                    field: 'export.' + portal + '.exp',
                    disableCellClick: true,
                    width: ColumnWidths.s,
                    type: 'singleselect',
                    data: sIntExportStatus,
                    renderCell(params) {
                        const portalId = parseFloat(params?.colDef?.field.replace('export.', ''));
                        const exportStatus = params?.row?.export && params?.row?.export[portalId]?.exp;
                        const exportError = params?.row?.export && params?.row?.export[portalId]?.err;
                        const exportElement = getElementFromArray(sStatusExport, exportStatus, 'i', {
                            i: 0,
                            t: 'Keine Exportinformationen verfügbar',
                            icon: 'mdi:help-circle',
                            iconColor: 'text.disabled',
                        });

                        return (
                            <Tooltip title={formatText(exportElement?.t, !checkUndefinedNullOrEmpty(exportError) ? '\nFehler: ' + exportError : '')} PopperProps={{ sx: { whiteSpace: 'pre' } }}>
                                <span>
                                    <IconButton
                                        disabled={!isDatenexport()}
                                        onClick={() => {
                                            additional?.changeExportStatusPortal(params?.row?.id, portalId, exportStatus !== 2);
                                        }}
                                    >
                                        <Badge
                                            overlap="circular"
                                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                            badgeContent={
                                                !checkUndefinedNullOrEmpty(exportError) ? (
                                                    <Iconify color="error.main" icon="mdi:alert-circle" sx={{ backgroundColor: '#fff', borderRadius: 10 }} width={12.5} height={12.5} />
                                                ) : undefined
                                            }
                                        >
                                            <Iconify color={exportElement?.iconColor} icon={exportElement?.icon} />
                                        </Badge>
                                    </IconButton>
                                </span>
                            </Tooltip>
                        );
                    },
                } as DataGridEnrichedColDef;
            })
            .sort((a, b) => a.headerName.toLowerCase().localeCompare(b.headerName.toLowerCase()))
            .sort((a, b) => a.groupSort.toLowerCase().localeCompare(b.groupSort.toLowerCase())),
        {
            headerName: 'Reserviert',
            group: 'Fahrzeugdaten',
            field: 'isRes',
            width: ColumnWidths.xs,
            type: 'singleselect',
            data: sIntReserviertStatus,
            renderCell(params) {
                const resStatus = getElementFromArray(sIntReserviertStatus, params?.row?.isRes, 'i', undefined);
                return params?.row?.isRes ? (
                    <Tooltip title={resStatus?.t}>
                        <Iconify color={resStatus?.iconColor} icon={resStatus?.icon} />
                    </Tooltip>
                ) : (
                    '-'
                );
            },
        },
        { headerName: 'Einkäufer', group: 'Fahrzeugdaten', field: 'usr_e.n', width: ColumnWidths.l },
        { headerName: 'Verkäufer', group: 'Fahrzeugdaten', field: 'usr_v.n', width: ColumnWidths.l },
        {
            headerName: 'Verkaufsdatum',
            group: 'Fahrzeugdaten',
            field: 'dv',
            width: ColumnWidths.s,
            type: 'date',
        },
        { headerName: 'Batterie enthalten', group: 'e-Mobilität', field: 'bat', type: 'singleselect', data: sIntBattery, width: ColumnWidths.m },
        { headerName: 'Batteriekapazität', group: 'e-Mobilität', field: 'batCap', type: 'decimal', einheit: 'kWh', width: ColumnWidths.s },
        { headerName: 'Range Extender', group: 'e-Mobilität', field: 'rex', type: 'check', width: ColumnWidths.xs },
        { headerName: 'Ladezeit bei 11kW bis 80%', group: 'e-Mobilität', field: 'consumptions.chargingTime', type: 'decimal', einheit: 'min', width: ColumnWidths.s },
        { headerName: 'Schnellladezeit bei min. 50kW bis 80%', group: 'e-Mobilität', field: 'consumptions.chargingTimeFast', type: 'decimal', einheit: 'min', width: ColumnWidths.s },
        { headerName: 'Steckertypen', group: 'e-Mobilität', field: 'consumptions.plugTypes', type: 'multiselect', data: sIntPlugTypes, width: ColumnWidths.s },
        // Leads
        { headerName: 'Offene Leads', group: 'Leads', field: 'leads.offenAmount', width: ColumnWidths.s },
        { headerName: 'Beendete Leads', group: 'Leads', field: 'leads.beendetAmount', width: ColumnWidths.s },
        // Verbrauch WLTP
        {
            headerName: 'Verbrauch niedrig',
            group: 'Verbrauch WLTP',
            field: 'consumptions.w_cfl',
            type: 'decimal',
            unrounded: 1,
            width: ColumnWidths.m,
            renderCell: (params) => {
                const isElektro = [205];
                const motorfuerkraftstoff = params?.row?.imtr ? params?.row?.imtr : 0;
                const value = isElektro.includes(motorfuerkraftstoff) ? params?.row?.consumptions?.w_cpl : params?.row?.consumptions?.w_cfl;
                if (!checkUndefinedNullOrEmpty(value)) {
                    const stringBuilder = [];
                    if (!params?.row?.vb.en) stringBuilder.push('ca.');
                    stringBuilder.push((value || 0).toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 }));
                    stringBuilder.push(getFieldFromArray(sIntKraftstoff, 'e', params?.row?.vb.iks, 'i', undefined));
                    return stringBuilder.join(' ');
                }
            },
        },
        {
            headerName: 'Verbrauch mittel',
            group: 'Verbrauch WLTP',
            field: 'consumptions.w_cfm',
            type: 'decimal',
            unrounded: 1,
            width: ColumnWidths.m,
            renderCell: (params) => {
                const isElektro = [205];
                const motorfuerkraftstoff = params?.row?.imtr ? params?.row?.imtr : 0;
                const value = isElektro.includes(motorfuerkraftstoff) ? params?.row?.consumptions?.w_cpm : params?.row?.consumptions?.w_cfm;
                if (!checkUndefinedNullOrEmpty(value)) {
                    const stringBuilder = [];
                    if (!params?.row?.vb.en) stringBuilder.push('ca.');
                    stringBuilder.push((value || 0).toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 }));
                    stringBuilder.push(getFieldFromArray(sIntKraftstoff, 'e', params?.row?.vb.iks, 'i', undefined));
                    return stringBuilder.join(' ');
                }
            },
        },
        {
            headerName: 'Verbrauch hoch',
            group: 'Verbrauch WLTP',
            field: 'consumptions.w_cfh',
            type: 'decimal',
            unrounded: 1,
            width: ColumnWidths.m,
            renderCell: (params) => {
                const isElektro = [205];
                const motorfuerkraftstoff = params?.row?.imtr ? params?.row?.imtr : 0;
                const value = isElektro.includes(motorfuerkraftstoff) ? params?.row?.consumptions?.w_cph : params?.row?.consumptions?.w_cfh;
                if (!checkUndefinedNullOrEmpty(value)) {
                    const stringBuilder = [];
                    if (!params?.row?.vb.en) stringBuilder.push('ca.');
                    stringBuilder.push((value || 0).toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 }));
                    stringBuilder.push(getFieldFromArray(sIntKraftstoff, 'e', params?.row?.vb.iks, 'i', undefined));
                    return stringBuilder.join(' ');
                }
            },
        },
        {
            headerName: 'Verbrauch höchstwert',
            group: 'Verbrauch WLTP',
            field: 'consumptions.w_cfeh',
            type: 'decimal',
            unrounded: 1,
            width: ColumnWidths.m,
            renderCell: (params) => {
                const isElektro = [205];
                const motorfuerkraftstoff = params?.row?.imtr ? params?.row?.imtr : 0;
                const value = isElektro.includes(motorfuerkraftstoff) ? params?.row?.consumptions?.w_cpeh : params?.row?.consumptions?.w_cfeh;
                if (!checkUndefinedNullOrEmpty(value)) {
                    const stringBuilder = [];
                    if (!params?.row?.vb.en) stringBuilder.push('ca.');
                    stringBuilder.push((value || 0).toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 }));
                    stringBuilder.push(getFieldFromArray(sIntKraftstoff, 'e', params?.row?.vb.iks, 'i', undefined));
                    return stringBuilder.join(' ');
                }
            },
        },
        {
            headerName: 'Verbrauch kombiniert',
            group: 'Verbrauch WLTP',
            field: 'consumptions.w_cfc',
            type: 'decimal',
            unrounded: 1,
            width: ColumnWidths.m,
            renderCell: (params) => {
                const isElektro = [205];
                const motorfuerkraftstoff = params?.row?.imtr ? params?.row?.imtr : 0;
                const value = isElektro.includes(motorfuerkraftstoff) ? params?.row?.consumptions?.w_cpc : params?.row?.consumptions?.w_cfc;
                if (!checkUndefinedNullOrEmpty(value)) {
                    const stringBuilder = [];
                    if (!params?.row?.vb.en) stringBuilder.push('ca.');
                    stringBuilder.push((value || 0).toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 }));
                    stringBuilder.push(getFieldFromArray(sIntKraftstoff, 'e', params?.row?.vb.iks, 'i', undefined));
                    return stringBuilder.join(' ');
                }
            },
        },
        {
            headerName: 'Emission',
            group: 'Verbrauch WLTP',
            field: 'consumptions.w_co2ec',
            type: 'decimal',
            width: ColumnWidths.m,
            renderCell: (params) => {
                if (!checkUndefinedNullOrEmpty(params?.row?.consumptions?.w_co2ec)) {
                    const stringBuilder = [];
                    if (!params?.row?.vb.en) stringBuilder.push('ca.');
                    stringBuilder.push((params?.row?.consumptions?.w_co2ec || 0).toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 }));
                    stringBuilder.push('g/km');
                    return stringBuilder.join(' ');
                }
            },
        },
        {
            headerName: 'CO2-Klasse',
            group: 'Verbrauch WLTP',
            field: 'consumptions.co2class',
            width: ColumnWidths.m,
            renderCell: (params) => {
                const isPluginHybrid = [227, 228, 229];
                const motorfuerkraftstoff = params?.row?.imtr ? params?.row?.imtr : 0;
                return (
                    <Effizienz
                        type="step"
                        step={
                            effizienzSteps(true)?.find(
                                (e) => e?.t === (isPluginHybrid.includes(motorfuerkraftstoff) ? params?.row?.consumptions?.co2classWeighted : params?.row?.consumptions?.co2class)
                            )?.i as EffizienzClass
                        }
                        stepHeight={16}
                        wltp
                    />
                );
            },
        },
        { headerName: 'Euronorm', group: 'Verbrauch', field: 'vb.isn', width: ColumnWidths.m, type: 'singleselect', data: sIntSchadstoff },
        { headerName: 'Plakette', group: 'Verbrauch', field: 'vb.ipl', width: ColumnWidths.xs, type: 'singleselect', height: '1.5em', data: sIntPlakette },
        { headerName: 'Jahressteuer', group: 'Verbrauch', field: 'pr.jst', width: ColumnWidths.m, type: 'currency' },
        // Finanzierung
        { headerName: 'Laufzeit', group: 'Finanzierung', field: 'fd.fin.lz', width: ColumnWidths.s, type: 'decimal', einheit: 'Monate' },
        { headerName: 'Laufleistung', group: 'Finanzierung', field: 'fd.fin.km', width: ColumnWidths.s, type: 'decimal', einheit: 'km' },
        { headerName: 'Monatliche Rate', group: 'Finanzierung', field: 'fd.fin.mr', width: ColumnWidths.s, type: 'currency' },
        { headerName: 'Anzahlung', group: 'Finanzierung', field: 'fd.fin.az', width: ColumnWidths.s, type: 'currency' },
        { headerName: 'Schlussrate', group: 'Finanzierung', field: 'fd.fin.sr', width: ColumnWidths.s, type: 'currency' },
        { headerName: 'Effektiver Jahreszins', group: 'Finanzierung', field: 'fd.fin.ez', width: ColumnWidths.s, type: 'percent', min: 0, unrounded: 2 },
        { headerName: 'Sollzins p.a.', group: 'Finanzierung', field: 'fd.fin.sz', width: ColumnWidths.s, type: 'percent', min: 0, unrounded: 2 },
        { headerName: 'KSB', group: 'Finanzierung', field: 'fd.fin.iksb', width: ColumnWidths.s, type: 'singleselect', data: sFdKsb },
        { headerName: 'KSB Betrag', group: 'Finanzierung', field: 'fd.fin.ksbbd', width: ColumnWidths.s, type: 'currency', min: 0 },
        { headerName: 'Nettodarlehenbetrag', group: 'Finanzierung', field: 'fd.fin.ndb', width: ColumnWidths.s, type: 'currency', min: 0 },
        { headerName: 'Gültig bis', group: 'Finanzierung', field: 'fd.fin.gb', width: ColumnWidths.s, type: 'datetime' },
        { headerName: 'Bank', group: 'Finanzierung', field: 'fd.fin.bank', width: ColumnWidths.s },
        { headerName: 'Kommentar', group: 'Finanzierung', field: 'fd.fin.comment', width: ColumnWidths.s, type: 'multiline' },
        { headerName: 'FD übertragen', group: 'Finanzierung', field: 'fd.fin.up', width: ColumnWidths.xs, type: 'singleselect', data: sIntAktivStatus },
        // Verkaufsverstärker
        {
            headerName: 'Aktionsfahrzeug',
            group: 'Aktionen Homepage',
            field: 'aktionsfahrzeug',
            width: ColumnWidths.xs,
            hidden: !getVertrag('json', vertrage),
            type: 'singleselect',
            data: sIntAktivStatus,
        },
        {
            headerName: 'Aktionsname',
            group: 'Aktionen Homepage',
            field: 'tags',
            editableBulk: true,
            width: ColumnWidths.xs,
            hidden: !getVertrag('json', vertrage),
            info: "Fügen Sie Ihrem Fahrzeug eine Aktion hinzu (Text) - Diese wird im HOMEPAGE-TOOL unter dem Filter 'Aktion' verfügbar sein",
        },
        // mobile.de
        { headerName: 'Topinserat', group: 'mobile.de Verstärker', field: 'mobileTopinserat', width: ColumnWidths.xs, type: 'singleselect', data: sIntAktivStatus },
        { headerName: 'Blickfang', group: 'mobile.de Verstärker', field: 'mobileBlickfang', width: ColumnWidths.xs, type: 'singleselect', data: sIntAktivStatus },
        { headerName: 'Seite 1', group: 'mobile.de Verstärker', field: 'mobileSeiteEins', width: ColumnWidths.xs, type: 'singleselect', data: sIntAktivStatus },
        { headerName: 'Rotstiftpr.', group: 'mobile.de Verstärker', field: 'mobileRotstiftpreis', width: ColumnWidths.xs, type: 'singleselect', data: sIntAktivStatus },
        { headerName: 'Ad Turbo', group: 'mobile.de Verstärker', field: 'mobileAdTurbo', width: ColumnWidths.xs, type: 'singleselect', data: sIntAktivStatus },
        { headerName: 'eBay Kleinanz.', group: 'mobile.de Verstärker', field: 'mobileEbayKleinanzeigen', width: ColumnWidths.xs, type: 'singleselect', data: sIntAktivStatus },
        // Tracking
        {
            headerName: 'Position',
            group: 'Tracking',
            field: 'tracking.pos.pos',
            width: ColumnWidths.xs,
            type: 'decimal',
            min: 0,
            renderCell(params) {
                return params?.row?.tracking?.pos?.pos ? (
                    <Typography
                        component="span"
                        variant="body1"
                        sx={{
                            color:
                                params?.row?.tracking?.pos?.pos >= 100
                                    ? 'error.main'
                                    : params?.row?.tracking?.pos?.pos >= 50
                                      ? 'warning.main'
                                      : params?.row?.tracking?.pos?.pos >= 20
                                        ? 'warning.lightest'
                                        : undefined,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {params?.row?.tracking?.pos?.pos}
                    </Typography>
                ) : (
                    '-'
                );
            },
        },
        { headerName: 'Aufrufe', group: 'Tracking', field: 'tracking.pos.impressions', width: ColumnWidths.xs, type: 'decimal', min: 0 },
        { headerName: 'Parkungen', group: 'Tracking', field: 'tracking.pos.parkings', width: ColumnWidths.xs, type: 'decimal', min: 0 },
        { headerName: 'Anrufe', group: 'Tracking', field: 'tracking.pos.calls', width: ColumnWidths.xs, type: 'decimal', min: 0 },
        { headerName: 'Ausdrucke', group: 'Tracking', field: 'tracking.pos.prints', width: ColumnWidths.xs, type: 'decimal', min: 0 },
        { headerName: 'Mails', group: 'Tracking', field: 'tracking.pos.mails', width: ColumnWidths.xs, type: 'decimal', min: 0 },
        // Preise
        { headerName: 'Steuer', group: 'Preise', field: 'pr.vt', width: ColumnWidths.s, type: 'singleselect', data: sIntSteuer(getMwSt()) },
        { headerName: 'VK', group: 'Preise', field: 'pr.bvkd', width: ColumnWidths.s, type: 'currency', defaultSort: 'asc' },
        { headerName: 'VK Web', group: 'Preise', field: 'pr.vkinet', width: ColumnWidths.s, type: 'currency' },
        { headerName: 'EK', group: 'Preise', field: 'pr.bek', width: ColumnWidths.s, type: 'currency' },
        {
            headerName: 'Ist-Kosten',
            group: 'Preise',
            field: 'pr.kos',
            width: ColumnWidths.s,
            type: 'currency',
            renderCell(params) {
                {
                    const oldPreise: {
                        [index: string]: number;
                    } = {};
                    if (params?.row?.bh) {
                        for (let i = 0; i < params?.row?.bh.length; i++) {
                            let val = 0;
                            switch (params?.row?.bh[i].i) {
                                case 5:
                                    val = Math.round(params?.row?.bh[i].v * getMwSt(true) * 100) / 100;
                                    oldPreise.kostenGWGarantieIst = val;
                                    break;
                                case 6:
                                    val = Math.round(params?.row?.bh[i].v * getMwSt(true) * 100) / 100;
                                    oldPreise.kostenAufbereitungskostenIst = val;
                                    break;
                                case 7:
                                    val = Math.round(params?.row?.bh[i].v * getMwSt(true) * 100) / 100;
                                    oldPreise.kostenInspektionenIst = val;
                                    break;
                                case 8:
                                    val = Math.round(params?.row?.bh[i].v * getMwSt(true) * 100) / 100;
                                    oldPreise.kostenInstandsetzungIst = val;
                                    break;
                                case 10:
                                    val = Math.round(params?.row?.bh[i].v * getMwSt(true) * 100) / 100;
                                    oldPreise.kostenZulassungskostenIst = val;
                                    break;
                                case 11:
                                    val = Math.round(params?.row?.bh[i].v * getMwSt(true) * 100) / 100;
                                    oldPreise.kostenVermittlerprovisionenIst = val;
                                    break;
                                case 12:
                                    val = Math.round(params?.row?.bh[i].v * getMwSt(true) * 100) / 100;
                                    oldPreise.kostenZugabenIst = val;
                                    break;
                                case 13:
                                    val = Math.round(params?.row?.bh[i].v * getMwSt(true) * 100) / 100;
                                    oldPreise.kostenHandlerzinsbeteiligungIst = val;
                                    break;
                                case 14:
                                    val = Math.round(params?.row?.bh[i].v * getMwSt(true) * 100) / 100;
                                    oldPreise.kostenSonstigeAufwendungenIst = val;
                                    break;
                                case 16:
                                    val = Math.round(params?.row?.bh[i].v * getMwSt(true) * 100) / 100;
                                    oldPreise.kostenZinsbelastungIst = val;
                                    break;
                                case 91:
                                    val = Math.round(params?.row?.bh[i].v * getMwSt(true) * 100) / 100;
                                    oldPreise.kostenGutschriftIst = val;
                                    break;
                                case 94:
                                    val = Math.round(params?.row?.bh[i].v * getMwSt(true) * 100) / 100;
                                    oldPreise.kostenAFAIst = val;
                                    break;
                                case 96:
                                    val = Math.round(params?.row?.bh[i].v * getMwSt(true) * 100) / 100;
                                    oldPreise.anschlussgarantie = val;
                                    break;
                                default:
                                    break;
                            }
                        }
                    }

                    const kosten =
                        (oldPreise.kostenGWGarantieIst || 0) +
                        (oldPreise.kostenInstandsetzungIst || 0) +
                        (oldPreise.kostenAufbereitungskostenIst || 0) +
                        (oldPreise.kostenInspektionenIst || 0) +
                        (oldPreise.kostenSonstigeAufwendungenIst || 0) +
                        (oldPreise.kostenZulassungskostenIst || 0) +
                        (oldPreise.kostenZugabenIst || 0) +
                        (oldPreise.kostenVermittlerprovisionenIst || 0) +
                        (oldPreise.kostenHandlerzinsbeteiligungIst || 0) +
                        (oldPreise.kostenZinsbelastungIst || 0);

                    return kosten ? (
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {kosten.toLocaleString('de-DE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                    ) : (
                        '-'
                    );
                }
            },
        },
        {
            headerName: 'Bruttoertrag',
            group: 'Preise',
            field: 'pr.berg',
            width: ColumnWidths.s,
            type: 'currency',
            renderCell(params) {
                return (
                    <span style={{ color: params?.row?.pr?.berg <= 0 ? theme?.palette?.error?.main : undefined, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {!checkUndefinedNullOrEmpty(params?.row?.pr?.berg)
                            ? params?.row?.pr?.berg.toLocaleString('de-DE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 })
                            : '-'}
                    </span>
                );
            },
        },
        { headerName: 'UPE', group: 'Preise', field: 'pr.upe', width: ColumnWidths.s, type: 'currency' },
        // Standzeit Eins
        { headerName: 'Zugang Reel', group: 'Statistik', field: 'stat.zr', width: ColumnWidths.s, type: 'date' },
        { headerName: 'Eingangscheck', group: 'Statistik', field: 'stat.ec', width: ColumnWidths.s, type: 'date' },
        { headerName: 'In TÜV', group: 'Statistik', field: 'stat.tuv', width: ColumnWidths.s, type: 'date' },
        { headerName: 'TÜV Ok', group: 'Statistik', field: 'stat.tuvok', width: ColumnWidths.s, type: 'date' },
        {
            headerName: 'TÜV Tage',
            group: 'Statistik',
            field: 'stat.inTUF',
            width: ColumnWidths.s,
            renderCell(params) {
                return params?.row?.stat ? (
                    <span
                        style={{
                            color:
                                params?.row?.stat?.inTUF >= 90
                                    ? theme?.palette?.error?.main
                                    : params?.row?.stat?.inTUF >= 60
                                      ? theme?.palette?.warning?.main
                                      : params?.row?.stat?.inTUF >= 30
                                        ? theme?.palette?.warning?.lightest
                                        : undefined,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {params?.row?.stat?.inTUF ? params?.row?.stat?.inTUF + (params?.row?.stat?.inTUF === 1 ? ' Tag' : ' Tage') : '-'}
                    </span>
                ) : (
                    '-'
                );
            },
            valueFormatter(params) {
                return params?.value ? `${params?.value} Tag${params?.value === 1 ? ' ' : 'e'}` : '-';
            },
        },
        { headerName: 'In W&V', group: 'Statistik', field: 'stat.wv', width: ColumnWidths.s, type: 'date' },
        { headerName: 'W&V Ok', group: 'Statistik', field: 'stat.wvok', width: ColumnWidths.s, type: 'date' },
        {
            headerName: 'W&V Tage',
            group: 'Statistik',
            field: 'stat.inWV',
            width: ColumnWidths.s,
            renderCell(params) {
                return params?.row?.stat?.inWV ? (
                    <Typography
                        component="span"
                        variant="body1"
                        sx={{
                            color: params?.row?.stat?.inWV >= 90 ? 'error.main' : params?.row?.stat?.inWV >= 60 ? 'warning.main' : params?.row?.stat?.inWV >= 30 ? 'warning.lightest' : undefined,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {`${params?.row?.stat?.inWV} Tag${params?.row?.stat?.inWV === 1 ? ' ' : 'e'}`}
                    </Typography>
                ) : (
                    '-'
                );
            },
            valueFormatter(params) {
                return params?.value ? `${params?.value} Tag${params?.value === 1 ? ' ' : 'e'}` : '-';
            },
        },
        { headerName: 'In Werkstatt', group: 'Statistik', field: 'stat.w', width: ColumnWidths.s, type: 'date' },
        { headerName: 'Technik Ok', group: 'Statistik', field: 'stat.wok', width: ColumnWidths.s, type: 'date' },
        {
            headerName: 'Werkstatt Tage',
            group: 'Statistik',
            field: 'stat.inW',
            width: ColumnWidths.s,
            renderCell(params) {
                return params?.row?.stat?.inW ? (
                    <Typography
                        component="span"
                        variant="body1"
                        sx={{
                            color:
                                getIdFirma() === 11700
                                    ? params?.row?.stat?.inW >= 6
                                        ? 'error.main'
                                        : params?.row?.stat?.inW >= 4
                                          ? 'warning.main'
                                          : 'success.main'
                                    : params?.row?.stat?.inW >= 90
                                      ? 'error.main'
                                      : params?.row?.stat?.inW >= 60
                                        ? 'warning.main'
                                        : params?.row?.stat?.inW >= 30
                                          ? 'warning.lightest'
                                          : undefined,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {`${params?.row?.stat?.inW} Tag${params?.row?.stat?.inW === 1 ? ' ' : 'e'}`}
                    </Typography>
                ) : (
                    '-'
                );
            },
            valueFormatter(params) {
                return params?.value ? `${params?.value} Tag${params?.value === 1 ? ' ' : 'e'}` : '-';
            },
        },
        { headerName: 'In Werkstatt Extern', group: 'Statistik', field: 'stat.we', width: ColumnWidths.s, type: 'date' },
        { headerName: 'Technik Extern Ok', group: 'Statistik', field: 'stat.weok', width: ColumnWidths.s, type: 'date' },
        {
            headerName: 'Werkstatt Extern Tage',
            group: 'Statistik',
            field: 'stat.inWE',
            width: ColumnWidths.s,
            renderCell(params) {
                return params?.row?.stat?.inWE ? (
                    <Typography
                        component="span"
                        variant="body1"
                        sx={{
                            color: params?.row?.stat?.inWE >= 90 ? 'error.main' : params?.row?.stat?.inWE >= 60 ? 'warning.main' : params?.row?.stat?.inWE >= 30 ? 'warning.lightest' : undefined,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {`${params?.row?.stat?.inWE} Tag${params?.row?.stat?.inWE === 1 ? ' ' : 'e'}`}
                    </Typography>
                ) : (
                    '-'
                );
            },
            valueFormatter(params) {
                return params?.value ? `${params?.value} Tag${params?.value === 1 ? ' ' : 'e'}` : '-';
            },
        },
        { headerName: 'In Lack', group: 'Statistik', field: 'stat.l', width: ColumnWidths.s, type: 'date' },
        { headerName: 'Lack Ok', group: 'Statistik', field: 'stat.lok', width: ColumnWidths.s, type: 'date' },
        {
            headerName: 'Lack Tage',
            group: 'Statistik',
            field: 'stat.inL',
            width: ColumnWidths.s,
            renderCell(params) {
                return params?.row?.stat?.inL ? (
                    <Typography
                        component="span"
                        variant="body1"
                        sx={{
                            color:
                                getIdFirma() === 11700
                                    ? params?.row?.stat?.inL >= 6
                                        ? 'error.main'
                                        : params?.row?.stat?.inL >= 4
                                          ? 'warning.main'
                                          : 'success.main'
                                    : params?.row?.stat?.inL >= 90
                                      ? 'error.main'
                                      : params?.row?.stat?.inL >= 60
                                        ? 'warning.main'
                                        : params?.row?.stat?.inL >= 30
                                          ? 'warning.lightest'
                                          : undefined,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {`${params?.row?.stat?.inL} Tag${params?.row?.stat?.inL === 1 ? ' ' : 'e'}`}
                    </Typography>
                ) : (
                    '-'
                );
            },
            valueFormatter(params) {
                return params?.value ? `${params?.value} Tag${params?.value === 1 ? ' ' : 'e'}` : '-';
            },
        },
        { headerName: 'In Aufbereitung', group: 'Statistik', field: 'stat.a', width: ColumnWidths.s, type: 'date' },
        { headerName: 'Optik Ok', group: 'Statistik', field: 'stat.aok', width: ColumnWidths.s, type: 'date' },
        {
            headerName: 'Aufbereitung Tage',
            group: 'Statistik',
            field: 'stat.inA',
            width: ColumnWidths.s,
            renderCell(params) {
                return params?.row?.stat?.inA ? (
                    <Typography
                        component="span"
                        variant="body1"
                        sx={{
                            color:
                                getIdFirma() === 11700
                                    ? params?.row?.stat?.inA >= 5
                                        ? 'error.main'
                                        : params?.row?.stat?.inA >= 3
                                          ? 'warning.main'
                                          : 'success.main'
                                    : params?.row?.stat?.inA >= 90
                                      ? 'error.main'
                                      : params?.row?.stat?.inA >= 60
                                        ? 'warning.main'
                                        : params?.row?.stat?.inA >= 30
                                          ? 'warning.lightest'
                                          : undefined,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {`${params?.row?.stat?.inA} Tag${params?.row?.stat?.inA === 1 ? ' ' : 'e'}`}
                    </Typography>
                ) : (
                    '-'
                );
            },
            valueFormatter(params) {
                return params?.value ? `${params?.value} Tag${params?.value === 1 ? ' ' : 'e'}` : '-';
            },
        },
        { headerName: 'Verkaufsfertig', group: 'Statistik', field: 'stat.vkf', width: ColumnWidths.s, type: 'date' },
    ];
}

export default sSpaltenFahrzeugliste;
